import { InputWithLabel } from '@adotel/components'
import { useLazyQuery } from '@apollo/client'
import { CircularProgress } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { convertPersianNumbersToEnglishNumbers } from '../../helpers/numbers'
import { filterAccommodationsByRatePlanQuery } from '../../helpers/ratePlanSearch'
import { getTranslate } from '../../localization'
import {
  Accommodation,
  AccommodationDataWithRatePlans,
  RatePlanWithSelectoption
} from '../../types/accommodation'
import { ContractInput } from '../../types/contract'
import EntitySelectionFilters from './EntitySelectionFilters'
import { EntitySelectionItemsBox } from './EntitySelectionItemsBox'
import useStyles from './styles/EntitySelection.styles'

interface EntitySelectionProps {
  contract: ContractInput
  setcontract: (c: ContractInput) => void
  loadingForContract: boolean
  accommodationsData: AccommodationDataWithRatePlans[]
  setAccommodationsData: React.Dispatch<
    React.SetStateAction<AccommodationDataWithRatePlans[]>
  >
  providerCodeForGenerate: string
  isRoleAccommodation: boolean
}
export const EntitySelection: React.FC<EntitySelectionProps> = ({
  contract,
  loadingForContract,
  accommodationsData,
  setAccommodationsData,
  providerCodeForGenerate,
  isRoleAccommodation
}) => {
  const classes = useStyles()
  const { providercode: providercodeParams } = useParams<{
    providercode: string
  }>()

  const [filteredAccommodationsData, setFilteredAccommodationsData] = useState<
    AccommodationDataWithRatePlans[]
  >([])

  const [searchInputValue, setsearchInputValue] = useState<string>('')

  const [getAccommodations, { data: accommodations, loading: loadingAccs }] =
    useLazyQuery<{
      accommodations: Accommodation[]
    }>(ACCOMMODATIONS_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  useEffect(() => {
    getAccommodations()
  }, [loadingForContract])

  const [
    accommodationsListForSelectedTenant,
    setAccommodationsListForSelectedTenant
  ] = useState<Accommodation[]>([])

  useEffect(() => {
    if (isRoleAccommodation && accommodationsListForSelectedTenant.length > 0) {
      const shortAcc = accommodationsListForSelectedTenant[0].basicDetails
      const temp: AccommodationDataWithRatePlans[] = [
        {
          id: shortAcc.accommodationID || '',
          propertyCode: shortAcc.propertyCode,
          code: shortAcc.displayPropertyCode || '',
          label: shortAcc.name,
          tenantCode: shortAcc.tenantCode,
          tenantID: shortAcc.tenantId || '',
          defaultProviderCode: shortAcc.defaultProviderCode,
          value: shortAcc.propertyCode || '',
          ratePlans: []
        }
      ]

      setAccommodationsData(temp)
    }
  }, [accommodationsListForSelectedTenant.length, loadingForContract])

  useEffect(() => {
    if (providercodeParams && accommodations?.accommodations) {
      getAccommodations({
        variables: {
          providerCode: providercodeParams
        }
      })

      setAccommodationsListForSelectedTenant(accommodations.accommodations)
    } else if (
      accommodations?.accommodations &&
      accommodationsData.length > 0
    ) {
      getAccommodations({
        variables: {
          providerCode: providercodeParams
        }
      })

      setAccommodationsListForSelectedTenant(accommodations.accommodations)
    } else if (providerCodeForGenerate && accommodations?.accommodations) {
      setAccommodationsListForSelectedTenant(accommodations.accommodations)
    } else {
      setAccommodationsListForSelectedTenant([])
    }
  }, [accommodations?.accommodations, accommodationsData])

  useEffect(() => {
    if (loadingAccs || loadingForContract) {
      return
    }
    const propertyCodes = Array.from(
      new Set(contract.RatePlans.map((item) => item.DisplayPropertyCode))
    )

    if (
      accommodations?.accommodations &&
      providerCodeForGenerate.length == 0
      // accommodationsData &&
      // accommodationsData.length > 0 &&
      // accommodationsData[0].ratePlans &&
      // accommodationsData[0].ratePlans.length > 0
    ) {
      const alreadySelectedAccs: AccommodationDataWithRatePlans[] = []
      accommodations?.accommodations
        .filter((accommodation) =>
          propertyCodes.includes(
            accommodation.basicDetails.displayPropertyCode || ''
          )
        )
        .map((accommodation) => {
          alreadySelectedAccs.push({
            label: accommodation.basicDetails.name,
            value: accommodation.basicDetails.propertyCode,
            id: accommodation.basicDetails.accommodationID || '',
            propertyCode: accommodation.basicDetails.propertyCode,
            code: accommodation.basicDetails.propertyCode,
            ratePlans:
              accommodationsData
                .find(
                  (acc) => acc.value === accommodation.basicDetails.propertyCode
                )
                ?.ratePlans?.map((rateplan) => ({
                  id: rateplan.id,
                  name: rateplan.name,
                  displayRatePlanCode: rateplan.displayRatePlanCode,
                  ratePlanCode: rateplan.ratePlanCode,
                  displayRoomTypeCode: rateplan.displayRoomTypeCode,
                  roomTypeCode: rateplan.roomTypeCode,
                  numberOfRates: rateplan.numberOfRates,
                  boardType: rateplan.boardType,
                  accommodationID: rateplan.accommodationID,
                  roomTypeID: rateplan.roomTypeID,
                  roomTypeName: rateplan.roomTypeName,
                  type: rateplan.type,
                  deleted: rateplan.deleted,
                  active: rateplan.active,
                  providerCode: rateplan.providerCode,
                  providerName: rateplan.providerName,
                  selected:
                    contract.RatePlans.findIndex(
                      (rp) =>
                        rp.DisplayRatePlanCode ===
                          (rateplan.displayRatePlanCode || '') &&
                        rp.DisplayRoomTypeCode === rateplan.displayRoomTypeCode
                    ) > -1
                })) || [],
            tenantCode: accommodation.basicDetails.tenantCode,
            tenantID: accommodation.basicDetails.tenantId || '',
            defaultProviderCode: accommodation.basicDetails.defaultProviderCode
          })
        })

      setAccommodationsData(alreadySelectedAccs)
    }
  }, [
    loadingForContract,
    loadingAccs,
    contract.RatePlans.length > 0,
    accommodationsData &&
      accommodationsData.length > 0 &&
      accommodationsData[0].ratePlans &&
      accommodationsData[0].ratePlans.length > 0
  ])

  // when search input value is changed
  useEffect(() => {
    setFilteredAccommodationsData(
      filterAccommodationsByRatePlanQuery(accommodationsData, searchInputValue)
    )
  }, [searchInputValue, accommodationsData])

  const handleChangeAccommodationRatePlans = (
    callbackFunc: (
      currentData: RatePlanWithSelectoption[]
    ) => RatePlanWithSelectoption[],
    index: number
  ) => {
    setAccommodationsData((currentData) => {
      const newRatePlans = callbackFunc(currentData[index].ratePlans || [])
      return [
        ...currentData.slice(0, index),
        {
          ...currentData[index],
          ratePlans: newRatePlans
        },
        ...currentData.slice(index + 1)
      ]
    })
  }

  return (
    <div className={classes.root}>
      <EntitySelectionFilters
        accs={accommodationsData}
        setaccs={setAccommodationsData}
        data={accommodationsListForSelectedTenant}
        loadingAccs={loadingAccs}
        setinputVal={setsearchInputValue}
        isRoleAccommodation={isRoleAccommodation}
      />
      <div className={classes.mainInput}>
        {accommodationsData.length > 0 && (
          <InputWithLabel
            placeholder={getTranslate(
              'Search in Room types, Rateplans or Rateplans Codes'
            )}
            autoComplete="off"
            noHelperText
            inputSize="small"
            startIcon={<SearchIcon />}
            value={searchInputValue}
            // While accommodations or the contract are loading, searching
            // would filter a stale/empty list and look broken — hold the
            // input until the data is there (same guard as the Bulk screen).
            disabled={loadingAccs || loadingForContract}
            onChange={(event) => {
              setsearchInputValue(
                convertPersianNumbersToEnglishNumbers(event.currentTarget.value)
              )
            }}
          />
        )}
      </div>

      {!(loadingAccs || loadingForContract) ? (
        <>
          {accommodationsData.length > 0 &&
            filteredAccommodationsData.map((acc) => {
              // The filtered list can drop accommodations, so its indices no
              // longer line up with accommodationsData — resolve the real
              // index of this accommodation before mutating its rate plans.
              const sourceIndex = accommodationsData.findIndex(
                (source) => source.value === acc.value
              )
              if (sourceIndex === -1) return null
              return (
                <EntitySelectionItemsBox
                  key={acc.id + sourceIndex}
                  accData={acc}
                  onChangeAccommodationRatePlans={(callbackFunc) =>
                    handleChangeAccommodationRatePlans(
                      callbackFunc,
                      sourceIndex
                    )
                  }
                  providerCode={providercodeParams}
                />
              )
            })}
        </>
      ) : (
        <CircularProgress
          color="primary"
          className={classes.CircularProgress}
        />
      )}
    </div>
  )
}

export default EntitySelection
